import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { githubApi, GitHubConnection, GitHubOrg, GitHubRepo } from '@/lib/api/github';
import { openOAuthPopup } from '@/lib/connector-oauth';
import toast from 'react-hot-toast';
import { Github, Loader2, Plus, Search, Lock, Unlock, Building, User, AlertTriangle, ChevronRight } from 'lucide-react';

interface GitHubImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

type Step = 'select-connection' | 'select-repo' | 'configure';

export default function GitHubImportModal({
  isOpen,
  onClose,
  onSuccess,
}: GitHubImportModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  // True while the OAuth popup is open AND while we're refreshing the
  // connections list afterwards. See github-modal.tsx for context — this
  // covers the embed-context delay so the modal shows a loading state
  // instead of its pre-OAuth view during the popup → result gap.
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [step, setStep] = useState<Step>('select-connection');

  // Connection state
  const [connections, setConnections] = useState<GitHubConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [selectedConnection, setSelectedConnection] = useState<GitHubConnection | null>(null);

  // Repo selection state
  const [organizations, setOrganizations] = useState<GitHubOrg[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [repositories, setRepositories] = useState<GitHubRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingRepos, setLoadingRepos] = useState(false);

  // Config state
  const [branch, setBranch] = useState('main');
  const [branches, setBranches] = useState<string[]>([]);
  const [projectName, setProjectName] = useState('');

  // Load connections on mount
  useEffect(() => {
    if (isOpen) {
      setStep('select-connection');
      loadConnections();
    }
  }, [isOpen]);

  // Load orgs when connection changes
  useEffect(() => {
    if (selectedConnectionId) {
      const conn = connections.find((c) => c.id === selectedConnectionId);
      setSelectedConnection(conn || null);
      if (conn) {
        loadOrganizations(selectedConnectionId);
        setSelectedOwner(conn.metadata?.login || '');
      }
    }
  }, [selectedConnectionId, connections]);

  // Load repos when owner changes
  useEffect(() => {
    if (selectedConnectionId && selectedOwner) {
      loadRepositories();
    }
  }, [selectedConnectionId, selectedOwner]);

  const loadConnections = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await githubApi.listConnections(token);
      setConnections(data);
      if (data.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load GitHub connections', error);
    }
  };

  const loadOrganizations = async (connectionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const orgs = await githubApi.listOrganizations(token, connectionId);
      setOrganizations(orgs);
    } catch (error) {
      console.error('Failed to load organizations', error);
    }
  };

  const loadRepositories = async () => {
    setLoadingRepos(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const repos = await githubApi.listRepositories(token, selectedConnectionId, selectedOwner);
      setRepositories(repos);
    } catch (error) {
      console.error('Failed to load repositories', error);
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadBranches = async (owner: string, repo: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const branchList = await githubApi.listBranches(token, selectedConnectionId, owner, repo);
      setBranches(branchList.map((b: { name: string }) => b.name));
    } catch (error) {
      console.error('Failed to load branches', error);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const { authUrl } = await githubApi.initiateOAuth(token, undefined, undefined, '/connections/oauth/callback');
      const prevIds = new Set(connections.map((c) => c.id));
      setConnectingOAuth(true);
      try {
        const result = await openOAuthPopup(authUrl, {
          fallbackCheck: async () => {
            const t = await getIdToken();
            if (!t) return null;
            const data = await githubApi.listConnections(t);
            const newConn = data.find((c) => !prevIds.has(c.id));
            return newConn ? { success: true, connectionId: newConn.id } : null;
          },
        });
        if (result.success) {
          await loadConnections();
        }
      } finally {
        setConnectingOAuth(false);
      }
    } catch (error) {
      console.error('Failed to initiate GitHub OAuth', error);
      toast.error('Failed to connect GitHub');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRepo = async (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setProjectName(repo.name);
    setBranch(repo.default_branch);
    await loadBranches(repo.owner.login, repo.name);
    setStep('configure');
  };

  const handleImport = async () => {
    if (!selectedRepo || !selectedConnectionId) {
      toast.error('Please select a repository');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const project = await githubApi.importRepo(token, {
        connectionId: selectedConnectionId,
        owner: selectedRepo.owner.login,
        repoName: selectedRepo.name,
        branch,
        projectName: projectName || selectedRepo.name,
      });
      toast.success('Repository imported successfully!');
      onSuccess(project.id);
      onClose();
    } catch (error: any) {
      console.error('Failed to import repository', error);
      toast.error(error.response?.data?.message || 'Failed to import repository');
    } finally {
      setLoading(false);
    }
  };

  const filteredRepos = searchQuery
    ? repositories.filter(
        (r) =>
          r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : repositories;

  const renderSelectConnection = () => (
    <div className="space-y-4">
      {connections.length === 0 ? (
        <div className="text-center py-8">
          <Github className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No GitHub accounts connected</p>
          <Button onClick={handleConnectGitHub} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Github className="h-4 w-4 mr-2" />}
            Connect GitHub Account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GitHub Account</Label>
            <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select GitHub account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={conn.metadata?.avatar_url} />
                        <AvatarFallback>{conn.metadata?.login?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span>{conn.metadata?.login || conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="sm" onClick={handleConnectGitHub} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Add another account
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => setStep('select-repo')} disabled={!selectedConnectionId}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderSelectRepo = () => (
    <div className="min-w-0 space-y-4">
      {/* Owner selection */}
      <div className="space-y-2">
        <Label>Repository Owner</Label>
        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger>
            <SelectValue placeholder="Select owner" />
          </SelectTrigger>
          <SelectContent>
            {selectedConnection?.metadata?.login && (
              <SelectItem value={selectedConnection.metadata.login}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedConnection.metadata.login}</span>
                  <span className="text-muted-foreground text-xs">(Personal)</span>
                </div>
              </SelectItem>
            )}
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.login}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={org.avatar_url} />
                    <AvatarFallback><Building className="h-3 w-3" /></AvatarFallback>
                  </Avatar>
                  <span>{org.login}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search repositories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Repository list */}
      <div className="max-h-[320px] space-y-1 overflow-y-auto overflow-x-hidden rounded-xl border border-border/60 bg-card/40 p-1.5">
        {loadingRepos ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading repositories…
          </div>
        ) : filteredRepos.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {searchQuery ? 'No repositories match your search' : 'No repositories found'}
          </div>
        ) : (
          filteredRepos.map((repo) => (
            <button
              key={repo.id}
              onClick={() => handleSelectRepo(repo)}
              className="group flex w-full min-w-0 items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/50 text-muted-foreground">
                {repo.private ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
              </span>
              <span className="min-w-0 flex-1 overflow-hidden">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">{repo.name}</span>
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {repo.private ? 'Private' : 'Public'}
                  </span>
                </span>
                {repo.description && (
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {repo.description}
                  </span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-transparent transition-colors group-hover:text-muted-foreground" />
            </button>
          ))
        )}
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-connection')}>
          Back
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );

  const renderConfigure = () => (
    <div className="min-w-0 space-y-4">
      {selectedRepo && (
        <div className="min-w-0 rounded-xl border border-border/60 bg-muted/40 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <Github className="h-4 w-4 shrink-0" />
            <span className="truncate font-medium">{selectedRepo.full_name}</span>
            {selectedRepo.private && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          </div>
          {selectedRepo.description && (
            <p className="mt-1 break-words text-sm text-muted-foreground">{selectedRepo.description}</p>
          )}
        </div>
      )}

      {/* Deployment support */}
      <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs">
        <div className="mb-1.5 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span className="font-medium text-foreground">Deployment support</span>
        </div>
        <p className="leading-relaxed text-muted-foreground">
          Static web apps (Vite, React) can be deployed to a live URL. Next.js and Python projects
          can be developed in the editor, but can't be deployed yet.
        </p>
      </div>

      {/* Project name */}
      <div className="space-y-2">
        <Label>Project Name</Label>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="My Project"
        />
      </div>

      {/* Branch */}
      <div className="space-y-2">
        <Label>Branch</Label>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger>
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.length > 0 ? (
              branches.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))
            ) : (
              <SelectItem value={branch}>{branch}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-repo')}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={loading || !projectName}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Import Repository
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <Github className="h-4 w-4" />
            </span>
            Import from GitHub
          </DialogTitle>
          <DialogDescription>
            {step === 'select-connection' && 'Select a GitHub account to import from'}
            {step === 'select-repo' && 'Choose a repository to import'}
            {step === 'configure' && 'Configure your import settings'}
          </DialogDescription>
        </DialogHeader>

        {connectingOAuth ? (
          <div className="flex flex-col items-center justify-center text-center py-10 space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Connecting to GitHub…</p>
              <p className="text-xs text-muted-foreground mt-1">
                Finishing authentication. This will only take a moment.
              </p>
            </div>
          </div>
        ) : (
          <>
            {step === 'select-connection' && renderSelectConnection()}
            {step === 'select-repo' && renderSelectRepo()}
            {step === 'configure' && renderConfigure()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
